import { useState } from "react";

import { IconButton, Popover, PopoverTrigger } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import type { GoogleAdsImportedLinkage } from "app/services/keyword-groups/utils/googleAdsImportedKeywordGroups";

import {
    GoogleAdsActionsListItem,
    GoogleAdsActionsMixedDescription,
    GoogleAdsActionsPopoverContent,
} from "./GoogleAdsActionsMenuStyle";
import type { GoogleAdsActionType } from "./google-ads-actions.types";
import { useGoogleAdsSelectionAction } from "./use-google-ads-selection-action";

interface IGoogleAdsActionsMenuProps {
    // Undefined for manual/legacy lists (no stored Google Ads linkage) - the control renders
    // nothing in that case, per SIM-112's "hidden, not disabled" requirement.
    linkage: GoogleAdsImportedLinkage | undefined;
    selectedKeywords: string[];
    isDisabled?: boolean;
    onGoogleAdsAction: (actionType: GoogleAdsActionType, keywords: string[], linkage: GoogleAdsImportedLinkage) => void;
}

const normalizeKeyword = (keyword: string): string => keyword.toLowerCase().trim();

const formatKeywordCount = (count: number): string => `${count} ${count === 1 ? "keyword" : "keywords"}`;

export const GoogleAdsActionsMenu = ({
    linkage,
    selectedKeywords,
    isDisabled = false,
    onGoogleAdsAction,
}: IGoogleAdsActionsMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);

    // Split the selection by import origin: keywords in the import snapshot were pushed from
    // Google Ads, so they offer "Remove"; keywords added to the list afterwards aren't in Google
    // Ads yet, so they offer "Add". A single keyword therefore only ever gets one of the two.
    const importedKeywordSet = new Set((linkage?.importedKeywords ?? []).map(normalizeKeyword));
    const removeEligibleKeywords = selectedKeywords.filter((keyword) =>
        importedKeywordSet.has(normalizeKeyword(keyword)),
    );
    const addEligibleKeywords = selectedKeywords.filter(
        (keyword) => !importedKeywordSet.has(normalizeKeyword(keyword)),
    );

    const isMixedSelection = addEligibleKeywords.length > 0 && removeEligibleKeywords.length > 0;

    const { dispatchAction } = useGoogleAdsSelectionAction({
        onAuthorized: (actionType) => {
            if (!linkage) {
                return;
            }
            const keywordsForAction =
                actionType === "remove"
                    ? removeEligibleKeywords
                    : actionType === "add"
                      ? addEligibleKeywords
                      : selectedKeywords;
            if (keywordsForAction.length === 0) {
                return;
            }
            onGoogleAdsAction(actionType, keywordsForAction, linkage);
        },
    });

    if (!linkage) {
        return null;
    }

    const actionButtonsType = isDisabled ? "flat" : "invertedTransparent";

    const handleActionClick = (actionType: GoogleAdsActionType) => () => {
        setIsOpen(false);
        dispatchAction(actionType);
    };

    return (
        <Popover
            open={isOpen}
            onOpenChange={setIsOpen}
            placement="bottom-end"
        >
            <PopoverTrigger asChild>
                <IconButton
                    type={actionButtonsType}
                    iconName="google"
                    iconSize="xs"
                    isDisabled={isDisabled}
                    data-automation="monitor-keywords-selection-google-ads-actions"
                >
                    Google Ads Actions
                </IconButton>
            </PopoverTrigger>
            <GoogleAdsActionsPopoverContent data-automation="monitor-keywords-selection-google-ads-actions-menu">
                {isMixedSelection ? (
                    <>
                        <GoogleAdsActionsMixedDescription data-automation="monitor-keywords-selection-google-ads-mixed-description">
                            <Text fontVariant="caption">
                                {`Mixed selection: ${formatKeywordCount(removeEligibleKeywords.length)} ${
                                    removeEligibleKeywords.length === 1 ? "is" : "are"
                                } in Google Ads and ${addEligibleKeywords.length} ${
                                    addEligibleKeywords.length === 1 ? "is" : "are"
                                } not. What would you like to do?`}
                            </Text>
                        </GoogleAdsActionsMixedDescription>
                        <GoogleAdsActionsListItem
                            onClick={handleActionClick("add")}
                            data-automation="monitor-keywords-selection-google-ads-add-all"
                        >
                            <Stack>
                                <Text fontVariant="input2">Add all to Google Ads</Text>
                                <Text fontVariant="caption">{`${formatKeywordCount(addEligibleKeywords.length)} will be added`}</Text>
                            </Stack>
                        </GoogleAdsActionsListItem>
                        <GoogleAdsActionsListItem
                            onClick={handleActionClick("remove")}
                            data-automation="monitor-keywords-selection-google-ads-remove-all"
                        >
                            <Stack>
                                <Text fontVariant="input2">Remove all from Google Ads</Text>
                                <Text fontVariant="caption">{`${formatKeywordCount(removeEligibleKeywords.length)} will be removed`}</Text>
                            </Stack>
                        </GoogleAdsActionsListItem>
                    </>
                ) : (
                    <>
                        {addEligibleKeywords.length > 0 ? (
                            <GoogleAdsActionsListItem
                                onClick={handleActionClick("add")}
                                data-automation="monitor-keywords-selection-google-ads-add-keyword"
                            >
                                <Text fontVariant="input2">Add to Google Ads</Text>
                            </GoogleAdsActionsListItem>
                        ) : null}
                        {removeEligibleKeywords.length > 0 ? (
                            <GoogleAdsActionsListItem
                                onClick={handleActionClick("remove")}
                                data-automation="monitor-keywords-selection-google-ads-remove-keyword"
                            >
                                <Text fontVariant="input2">Remove from Google Ads</Text>
                            </GoogleAdsActionsListItem>
                        ) : null}
                    </>
                )}
                <GoogleAdsActionsListItem
                    onClick={handleActionClick("negative")}
                    data-automation="monitor-keywords-selection-google-ads-add-negative"
                >
                    <Text fontVariant="input2">Add to negative keywords</Text>
                </GoogleAdsActionsListItem>
            </GoogleAdsActionsPopoverContent>
        </Popover>
    );
};
